<script lang="ts" setup>
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import AlertMessage from "@/components/messages/AlertMessage.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import OrderPayments from "@/components/order/OrderPayments.vue";
import OrderStatusSelect from "@/components/selects/OrderStatusSelect.vue";
import { useChangeOrderStatus, useOrderPayments } from "@/composables";
import { useError } from "@/composables/useError";
import { AlertMessageType } from "@/constants/components";
import { OrderPaymentsVariant, OrderStatus } from "@/constants/order";
import {
  getChangedOrderPayments,
  getOrderPaymentInvoiceIds,
  isInvoiceDeletedByStatusChange,
  isOrderPaymentsChanged,
} from "@/utils/orderPayments";
import OrderFile from "@/views/orders/edit/components/OrderFile.vue";

import type { VForm } from "vuetify/components";
import type { OrderDto, OrderPayment, PatchOrderDto } from "@/api/orders/dto/order.dto";

const { t } = useI18n();
const { createMandatoryOrderPayment } = useOrderPayments();
const { scrollToError } = useError();
const { isSaving, uploadTonuFile, patchOrderStatus } = useChangeOrderStatus();

const isOpen = defineModel<boolean>({ default: false });

const props = defineProps<{
  order: OrderDto;
}>();

const emit = defineEmits<{
  saved: [];
}>();

const formRef = useTemplateRef<VForm | null>("form");

const status = ref<OrderStatus>(props.order.status ?? OrderStatus.unassigned);
const payments = ref<OrderPayment[]>([...props.order.orderPayments]);
const tonuFile = ref<File | null>(null);
const uploadedTonuFile = ref<File | null>(null);
const rejectionMessage = ref<string | null>(null);

const isRejected = computed<boolean>(() => rejectionMessage.value !== null);

const isStatusChanged = computed<boolean>(() => status.value !== props.order.status);

const isPaymentsChanged = computed<boolean>(() => {
  return isOrderPaymentsChanged(props.order.orderPayments, payments.value);
});

const isTonuFileVisible = computed<boolean>(() => status.value === OrderStatus.tonu && !isRejected.value);

const isSaveDisabled = computed<boolean>(() => {
  return isRejected.value || (!isStatusChanged.value && !isPaymentsChanged.value && !tonuFile.value);
});

const patchData = computed<PatchOrderDto>(() => {
  const data: PatchOrderDto = {};
  const orderPayments = getChangedOrderPayments(props.order.orderPayments, payments.value);

  if (isStatusChanged.value) {
    data.status = status.value;
  }

  if (orderPayments.length > 0) {
    data.orderPayments = orderPayments;
  }

  return data;
});

const deletedInvoiceIds = computed<string[]>(() => {
  if (!isInvoiceDeletedByStatusChange(props.order.status, status.value)) {
    return [];
  }

  return getOrderPaymentInvoiceIds(props.order.orderPayments);
});

function close() {
  isOpen.value = false;
}

async function save() {
  const orderId = props.order.id;

  if (!formRef.value || !orderId) {
    return;
  }

  const result = await formRef.value.validate();

  if (!result.valid) {
    scrollToError();
    return;
  }

  if (tonuFile.value && tonuFile.value !== uploadedTonuFile.value) {
    const isUploaded = await uploadTonuFile(orderId, tonuFile.value);

    if (!isUploaded) {
      return;
    }

    uploadedTonuFile.value = tonuFile.value;
  }

  if (isStatusChanged.value || isPaymentsChanged.value) {
    const { success, rejectionMessage: message } = await patchOrderStatus(orderId, patchData.value);

    if (!success) {
      rejectionMessage.value = message;
      return;
    }
  }

  emit("saved");
  close();
}

watch(status, (value) => {
  payments.value = createMandatoryOrderPayment(payments.value, value);

  if (value !== OrderStatus.tonu) {
    tonuFile.value = null;
  }
});
</script>

<template>
  <omni-dialog
    v-model="isOpen"
    :primary-action-label="t('general.save')"
    :primary-button-disabled="isSaveDisabled"
    :primary-button-loading="isSaving"
    :secondary-action-label="t('general.cancel')"
    :title="t('orders.change-order-status')"
    :width="720"
    @primary-action:click="save"
    @secondary-action:click="close"
  >
    <v-form ref="form" class="d-flex flex-column row-gap-6 pt-4">
      <order-status-select v-model="status" :readonly="isRejected" :status-from-api="props.order.status" />

      <order-payments
        v-model="payments"
        :order-status="status"
        :readonly="isRejected"
        :variant="OrderPaymentsVariant.Cards"
        can-restore
      >
        <template v-if="isTonuFileVisible" #mandatory-payment>
          <order-file v-model="tonuFile" :label="t('orders.tonu-file')" />
        </template>
      </order-payments>

      <alert-message
        v-for="invoiceId in deletedInvoiceIds"
        :key="invoiceId"
        :type="AlertMessageType.warning"
        data-qa="order-invoice-deleted-alert"
      >
        {{ t("trip.invoice-will-be-deleted", { invoice: invoiceId }) }}
      </alert-message>

      <alert-message
        v-if="isRejected"
        :type="AlertMessageType.danger"
        data-qa="change-order-status-rejected-alert"
      >
        {{ rejectionMessage }}
      </alert-message>
    </v-form>
  </omni-dialog>
</template>
